import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowSquareOut, FileArrowDown, House, ImageSquare, Lightning, Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, adminHeaders, getSecret, fileUrl } from "@/lib/api";
import { Lightbox } from "@/components/Lightbox";
import { RefreshButton } from "@/components/RefreshButton";

function OrderChip({ label, done, busy, onToggle, testid }) {
    return (
        <button
            data-testid={testid}
            onClick={() => onToggle(!done)}
            disabled={busy}
            title={done ? "Klik untuk tandai belum diorder" : "Klik untuk tandai sudah diorder"}
            className={`text-xs px-2.5 py-1.5 border flex items-center gap-1.5 transition-colors disabled:opacity-50 ${
                done
                    ? "border-green-600 text-green-700 bg-green-50 hover:bg-green-100"
                    : "border-amber-500 text-amber-700 bg-amber-50 hover:bg-amber-100"
            }`}
        >
            <span className={`w-3.5 h-3.5 border flex items-center justify-center text-[10px] ${done ? "bg-green-600 border-green-600 text-white" : "border-amber-500"}`}>
                {done && "✓"}
            </span>
            {label}: {done ? "Sudah diorder" : "Belum diorder"}
        </button>
    );
}

export default function ProduksiPage() {
    const navigate = useNavigate();
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusBusy, setStatusBusy] = useState("");
    const [lightbox, setLightbox] = useState(null);
    const [expandedRenders, setExpandedRenders] = useState(() => new Set());
    const toggleRenders = (id) =>
        setExpandedRenders((s) => {
            const n = new Set(s);
            if (n.has(id)) n.delete(id);
            else n.add(id);
            return n;
        });
    const [showAdd, setShowAdd] = useState(false);
    const [properties, setProperties] = useState([]);
    const [addPropId, setAddPropId] = useState("");
    const [propRooms, setPropRooms] = useState([]);
    const [addBusy, setAddBusy] = useState("");

    const load = useCallback(async () => {
        const res = await api.get("/admin/produksi", { headers: adminHeaders() });
        setRooms(res.data);
    }, []);

    const toggleOrder = async (roomId, field, value) => {
        setStatusBusy(roomId + field);
        try {
            await api.patch(`/admin/rooms/${roomId}/status`, { field, value }, { headers: adminHeaders() });
            await load();
        } catch {
            toast.error("Gagal mengubah status order");
        } finally {
            setStatusBusy("");
        }
    };

    const moveToFinishing = async (roomId, target) => {
        setStatusBusy(roomId + target);
        try {
            await api.patch(`/admin/rooms/${roomId}/production-status`, { value: target }, { headers: adminHeaders() });
            toast.success(target === "finishing_budhi" ? "Masuk Finishing Budhi" : "Masuk Finishing Cibogo");
            await load();
        } catch {
            toast.error("Gagal memindahkan ke finishing");
        } finally {
            setStatusBusy("");
        }
    };

    const openAdd = async () => {
        setShowAdd(true);
        setAddPropId("");
        setPropRooms([]);
        try {
            const res = await api.get("/admin/properties", { headers: adminHeaders() });
            setProperties(res.data);
        } catch {
            toast.error("Gagal memuat properti");
        }
    };

    const pickProperty = async (propId) => {
        setAddPropId(propId);
        if (!propId) {
            setPropRooms([]);
            return;
        }
        try {
            const res = await api.get(`/admin/properties/${propId}/rooms`, { headers: adminHeaders() });
            setPropRooms(res.data);
        } catch {
            toast.error("Gagal memuat ruangan");
        }
    };

    const addToProduksi = async (roomId) => {
        setAddBusy(roomId);
        try {
            await api.patch(`/admin/rooms/${roomId}/production-status`, { value: "sedang_diproduksi" }, { headers: adminHeaders() });
            toast.success("Ruangan masuk daftar produksi");
            await load();
            if (addPropId) await pickProperty(addPropId);
        } catch {
            toast.error("Gagal menambahkan");
        } finally {
            setAddBusy("");
        }
    };

    useEffect(() => {
        if (!getSecret()) {
            navigate("/admin");
            return;
        }
        load()
            .catch(() => toast.error("Gagal memuat daftar produksi"))
            .finally(() => setLoading(false));
    }, [load, navigate]);

    return (
        <div className="min-h-screen flex flex-col" data-testid="produksi-page">
            <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-6xl mx-auto w-full px-6 py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <button
                            data-testid="produksi-back"
                            onClick={() => navigate("/admin")}
                            className="text-muted-foreground hover:text-primary transition-colors"
                            title="Kembali"
                        >
                            <ArrowLeft size={20} weight="light" />
                        </button>
                        <img src="/logo.png" alt="INERRE interior" className="h-10 object-contain" />
                    </div>
                    <div className="text-center">
                        <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-muted-foreground">Sedang Diproduksi</p>
                        <h1 className="font-serif text-2xl sm:text-3xl tracking-tight font-medium">Semua Produksi</h1>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <RefreshButton />
                        <button
                            data-testid="produksi-home-btn"
                            onClick={() => navigate("/admin")}
                            className="text-muted-foreground hover:text-primary transition-colors shrink-0"
                            title="Kembali ke pemindai"
                        >
                            <House size={20} weight="light" />
                        </button>
                    </div>
                </div>
            </header>

            <main className="flex-1 w-full max-w-6xl mx-auto px-6 py-6">
                <div className="mb-5">
                    <button
                        data-testid="produksi-add-btn"
                        onClick={openAdd}
                        className="bg-primary text-primary-foreground px-4 py-2.5 text-sm font-medium hover:bg-[#9c4a2a] transition-colors flex items-center gap-2"
                    >
                        <Plus size={16} weight="bold" /> Tambah Produksi
                    </button>
                </div>

                {showAdd && (
                    <div className="border border-border bg-white p-4 mb-6 space-y-3" data-testid="produksi-add-panel">
                        <div className="flex items-center justify-between">
                            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Tambah Ruangan ke Produksi</p>
                            <button data-testid="produksi-add-close" onClick={() => setShowAdd(false)} className="text-muted-foreground hover:text-primary">
                                <X size={18} />
                            </button>
                        </div>
                        <select
                            data-testid="produksi-add-prop"
                            value={addPropId}
                            onChange={(e) => pickProperty(e.target.value)}
                            className="w-full border border-border px-3 py-2 text-sm outline-none focus:border-primary bg-white"
                        >
                            <option value="">— Pilih proyek —</option>
                            {properties.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.name}
                                </option>
                            ))}
                        </select>
                        {addPropId && (
                            <div className="space-y-2" data-testid="produksi-add-rooms">
                                {propRooms.length === 0 && <p className="text-xs text-muted-foreground">Proyek ini belum punya ruangan.</p>}
                                {propRooms.map((r) => {
                                    const already = r.production_status === "sedang_diproduksi";
                                    return (
                                        <div key={r.id} className="flex items-center justify-between border border-border px-3 py-2" data-testid={`produksi-add-room-${r.id}`}>
                                            <span className="text-sm">
                                                {r.name}
                                                {already && <span className="ml-2 text-[10px] uppercase tracking-widest text-muted-foreground">(sudah di produksi)</span>}
                                            </span>
                                            <button
                                                data-testid={`produksi-add-room-btn-${r.id}`}
                                                onClick={() => addToProduksi(r.id)}
                                                disabled={already || addBusy === r.id}
                                                className="text-xs border border-primary text-primary px-3 py-1.5 hover:bg-primary hover:text-primary-foreground transition-colors disabled:opacity-40"
                                            >
                                                {already ? "Sudah masuk" : "+ Tambah"}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {loading ? (
                    <p className="text-sm text-muted-foreground" data-testid="produksi-loading">Memuat...</p>
                ) : rooms.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-16" data-testid="produksi-empty">
                        Tidak ada ruangan dengan status "Sedang Diproduksi".
                    </p>
                ) : (
                    <div className="space-y-6" data-testid="produksi-list">
                        {rooms.map((room) => {
                            const hasWarning = !room.gambar_kerja_link || !room.order_veneer_hpl || !room.order_hardware;
                            return (
                            <div
                                key={room.id}
                                className={`border ${hasWarning ? "border-red-300 bg-red-50/40" : "border-border bg-white"}`}
                                data-testid={`produksi-room-${room.id}`}
                            >
                                <div className="p-4 border-b border-border flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                    <div>
                                        <h3 className="font-serif text-xl sm:text-2xl tracking-tight font-medium" data-testid={`produksi-name-${room.id}`}>
                                            {room.name}
                                        </h3>
                                        <p className="text-xs text-muted-foreground mt-1" data-testid={`produksi-prop-${room.id}`}>
                                            {room.property_name}
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap gap-2 sm:justify-end sm:shrink-0">
                                        <button
                                            data-testid={`produksi-to-budhi-${room.id}`}
                                            onClick={() => moveToFinishing(room.id, "finishing_budhi")}
                                            disabled={statusBusy === room.id + "finishing_budhi"}
                                            className="flex-1 sm:flex-none text-center border border-primary text-primary px-3 py-2 text-xs hover:bg-primary hover:text-primary-foreground transition-colors disabled:opacity-50"
                                        >
                                            Masuk Finishing Budhi
                                        </button>
                                        <button
                                            data-testid={`produksi-to-cibogo-${room.id}`}
                                            onClick={() => moveToFinishing(room.id, "finishing_cibogo")}
                                            disabled={statusBusy === room.id + "finishing_cibogo"}
                                            className="flex-1 sm:flex-none text-center border border-primary text-primary px-3 py-2 text-xs hover:bg-primary hover:text-primary-foreground transition-colors disabled:opacity-50"
                                        >
                                            Masuk Finishing Cibogo
                                        </button>
                                        <a
                                            data-testid={`produksi-open-${room.id}`}
                                            href={`/a/${room.id}`}
                                            className="flex-1 sm:flex-none justify-center border border-border px-3 py-2 text-xs hover:border-primary hover:text-primary transition-colors flex items-center gap-1.5"
                                        >
                                            <ArrowSquareOut size={14} weight="light" /> Buka Ruangan
                                        </a>
                                    </div>
                                </div>
                                {hasWarning && (
                                    <p
                                        className="px-4 py-2 bg-red-100/70 border-b border-red-200 text-[11px] font-bold uppercase tracking-wider text-red-700"
                                        data-testid={`produksi-warning-${room.id}`}
                                    >
                                        Perlu perhatian: {!room.gambar_kerja_link && "gambar kerja belum ada"}
                                        {!room.gambar_kerja_link && (!room.order_veneer_hpl || !room.order_hardware) && " • "}
                                        {!room.order_veneer_hpl && "veneer+HPL belum diorder"}
                                        {!room.order_veneer_hpl && !room.order_hardware && " • "}
                                        {!room.order_hardware && "hardware belum diorder"}
                                    </p>
                                )}

                                <div className="p-4 space-y-4">
                                    <div>
                                        <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-2">Rendering</p>
                                        {room.renderings?.length > 0 ? (
                                            <>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid={`produksi-renderings-${room.id}`}>
                                                {(expandedRenders.has(room.id) || room.renderings.length <= 4 ? room.renderings : room.renderings.slice(0, 3)).map((path, i) => (
                                                    <button
                                                        key={path}
                                                        onClick={() => setLightbox({ images: room.renderings, index: i })}
                                                        className="aspect-[4/3] bg-secondary overflow-hidden border border-border block cursor-zoom-in"
                                                        data-testid={`produksi-rendering-${room.id}-${i}`}
                                                    >
                                                        <img
                                                            src={fileUrl(path)}
                                                            alt={`Rendering ${room.name} ${i + 1}`}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </button>
                                                ))}
                                                {!expandedRenders.has(room.id) && room.renderings.length > 4 && (
                                                    <button
                                                        onClick={() => toggleRenders(room.id)}
                                                        data-testid={`produksi-more-renderings-${room.id}`}
                                                        className="aspect-[4/3] border border-dashed border-border text-xs text-muted-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center"
                                                    >
                                                        +{room.renderings.length - 3} lainnya
                                                    </button>
                                                )}
                                            </div>
                                            {expandedRenders.has(room.id) && room.renderings.length > 4 && (
                                                <button
                                                    onClick={() => toggleRenders(room.id)}
                                                    data-testid={`produksi-less-renderings-${room.id}`}
                                                    className="text-xs text-primary hover:underline mt-2"
                                                >
                                                    Sembunyikan
                                                </button>
                                            )}
                                            </>
                                        ) : (
                                            <div className="aspect-[4/3] max-w-xs bg-secondary flex items-center justify-center border border-border" data-testid={`produksi-no-rendering-${room.id}`}>
                                                <ImageSquare size={28} weight="light" className="text-muted-foreground" />
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex flex-wrap gap-2">
                                        {room.me_link && (
                                            <a
                                                data-testid={`produksi-me-${room.id}`}
                                                href={room.me_link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="border border-border px-3 py-2 text-xs hover:border-primary hover:text-primary transition-colors flex items-center gap-1.5"
                                            >
                                                <Lightning size={14} weight="light" /> Gambar ME / Electrical
                                            </a>
                                        )}
                                        {room.gambar_kerja_link && (
                                            <a
                                                data-testid={`produksi-gk-${room.id}`}
                                                href={room.gambar_kerja_link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="border border-border px-3 py-2 text-xs hover:border-primary hover:text-primary transition-colors flex items-center gap-1.5"
                                            >
                                                <FileArrowDown size={14} weight="light" /> Gambar Kerja
                                                <span
                                                    className="ml-1 text-[10px] font-bold tracking-widest uppercase border border-primary text-primary px-1.5 py-0.5"
                                                    data-testid={`produksi-gk-version-${room.id}`}
                                                >
                                                    {room.gambar_kerja_version || "Tanpa versi"}
                                                </span>
                                            </a>
                                        )}
                                        {!room.me_link && !room.gambar_kerja_link && (
                                            <p className="text-xs text-muted-foreground" data-testid={`produksi-no-links-${room.id}`}>
                                                Belum ada link Google Drive.
                                            </p>
                                        )}
                                    </div>

                                    <div className="flex flex-wrap gap-2" data-testid={`produksi-orders-${room.id}`}>
                                        <OrderChip
                                            label="Veneer + HPL"
                                            done={room.order_veneer_hpl}
                                            busy={statusBusy === room.id + "order_veneer_hpl"}
                                            onToggle={(v) => toggleOrder(room.id, "order_veneer_hpl", v)}
                                            testid={`produksi-veneer-${room.id}`}
                                        />
                                        <OrderChip
                                            label="Hardware"
                                            done={room.order_hardware}
                                            busy={statusBusy === room.id + "order_hardware"}
                                            onToggle={(v) => toggleOrder(room.id, "order_hardware", v)}
                                            testid={`produksi-hardware-${room.id}`}
                                        />
                                    </div>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                )}
            </main>

            {lightbox && (
                <Lightbox
                    src={fileUrl(lightbox.images[lightbox.index])}
                    alt="Rendering"
                    onClose={() => setLightbox(null)}
                    onPrev={lightbox.images.length > 1 ? () => setLightbox((s) => ({ ...s, index: (s.index - 1 + s.images.length) % s.images.length })) : null}
                    onNext={lightbox.images.length > 1 ? () => setLightbox((s) => ({ ...s, index: (s.index + 1) % s.images.length })) : null}
                />
            )}
        </div>
    );
}
